/**
 * Uruguayan RUT (Registro Único Tributario) validation.
 *
 * Context: Jarix currently bills only from Uruguay (product decision
 * on 2026-04-19), so the Sprint 2 scope covers this jurisdiction only.
 * Other countries keep free-text tax_id without client-side validation;
 * they'll get their own validators when Jarix starts invoicing there.
 *
 * Format spec (DGI — Dirección General Impositiva):
 *   * 12 numeric digits.
 *   * Users enter it with any combination of dots, dashes or spaces;
 *     we normalize to raw digits before checking.
 *   * Display form canonical: "XXXXXXXXXXXX" (no separators) because
 *     NetSuite expects it like that. If in the future the UI wants a
 *     formatted display, `formatRUT` returns "XX.XXX.XXX.XXXX".
 *
 * Check-digit algorithm (official):
 *   * Take first 11 digits.
 *   * Multiply each by the weight table [4,3,2,9,8,7,6,5,4,3,2]
 *     from left to right.
 *   * Sum, take mod 11.
 *   * If sum mod 11 === 0 → check digit is 0.
 *   * Else → check digit = 11 - (sum mod 11). Values 10 and above
 *     are invalid (DGI reserves those and does not issue them).
 *
 * This matches the published DGI spec and known-valid RUT samples
 * (e.g. 210000100013 which belongs to the Presidency of Uruguay).
 */

const WEIGHTS = [4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] as const

/**
 * Strip anything that's not a digit. Safe to call on already-clean
 * input (idempotent). Returns '' on null/undefined.
 */
export function normalizeRUT(raw: string | null | undefined): string {
  if (!raw) return ''
  return String(raw).replace(/\D/g, '')
}

/**
 * Compute the expected check digit for a given RUT prefix (first 11
 * digits). Returns -1 when the prefix is not exactly 11 digits — that
 * way callers can distinguish "too short" from "wrong check digit".
 */
function expectedCheckDigit(eleven: string): number {
  if (!/^\d{11}$/.test(eleven)) return -1
  let sum = 0
  for (let i = 0; i < 11; i++) {
    sum += parseInt(eleven[i], 10) * WEIGHTS[i]
  }
  const mod = sum % 11
  if (mod === 0) return 0
  const check = 11 - mod
  // DGI never issues 10-or-higher — treat as invalid.
  return check >= 10 ? -1 : check
}

export interface RUTValidation {
  valid: boolean
  /** Normalized digits-only form — suitable to persist / send to ERP. */
  normalized: string
  /** Present only when valid === false. i18n-friendly short key. */
  reason?: 'empty' | 'length' | 'non_numeric' | 'check_digit'
  /** Human-readable description of the failure in Spanish, UI-ready. */
  message?: string
}

/**
 * Validate a RUT with full diagnosis.
 *
 * Does NOT throw — always returns a verdict object. The caller decides
 * how strict to be (required vs. optional, blocking vs. warning).
 */
export function validateRUT(raw: string | null | undefined): RUTValidation {
  const normalized = normalizeRUT(raw)

  if (normalized.length === 0) {
    return {
      valid: false,
      normalized,
      reason: 'empty',
      message: 'El RUT es obligatorio',
    }
  }

  if (!/^\d+$/.test(normalized)) {
    return {
      valid: false,
      normalized,
      reason: 'non_numeric',
      message: 'El RUT solo puede contener números, puntos o guiones',
    }
  }

  if (normalized.length !== 12) {
    return {
      valid: false,
      normalized,
      reason: 'length',
      message: `El RUT debe tener 12 dígitos (actualmente tiene ${normalized.length})`,
    }
  }

  const prefix = normalized.slice(0, 11)
  const actualCheck = parseInt(normalized.slice(11), 10)
  const expected = expectedCheckDigit(prefix)

  if (expected === -1 || expected !== actualCheck) {
    return {
      valid: false,
      normalized,
      reason: 'check_digit',
      message: 'Dígito verificador incorrecto',
    }
  }

  return { valid: true, normalized }
}

/**
 * Format a (normalized or raw) RUT as "XX.XXX.XXX.XXXX".
 * Returns the input unchanged when it's not 12 digits, so callers can
 * safely pipe any user input without guarding. Used for display only
 * — we persist and send the normalized form to the ERP.
 */
export function formatRUT(raw: string | null | undefined): string {
  const n = normalizeRUT(raw)
  if (n.length !== 12) return String(raw ?? '')
  // Segmentation: 2 . 3 . 3 . 4
  return `${n.slice(0, 2)}.${n.slice(2, 5)}.${n.slice(5, 8)}.${n.slice(8, 12)}`
}
